import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { Loader2, ArrowLeft, ChevronDown, ChevronRight } from "lucide-react";
import { api, formatApiError } from "@/api/client";
import { fmtMoney } from "@/utils/money";

/**
 * Phase PAY-1 — per-employee monthly breakdown. Expandable row per month
 * shows every session accrual (date, present count, per-student price,
 * teacher share %, gross, teacher amount). Salary amounts read `—` when
 * the caller lacks salary permissions.
 */
type Session = {
    accrual_id: string;
    session_id: string;
    group_id: string;
    session_date: string;
    present_count: number;
    per_student_lesson_price_minor: number;
    teacher_share_percent: number | null;
    gross_amount_minor: number | null;
    amount_minor: number | null;
    currency: string;
};
type Month = {
    month: number;
    accrued_minor: number | null;
    paid_minor: number | null;
    remaining_minor: number | null;
    sessions_count: number;
    sessions: Session[];
    salary_masked?: boolean;
};

export default function PayrollEmployeeDetail() {
    const { t } = useTranslation();
    const { eid } = useParams<{ eid: string }>();
    const [sp] = useSearchParams();
    const year = parseInt(sp.get("year") || new Date().getFullYear().toString());
    const [months, setMonths] = useState<Month[]>([]);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState<string | null>(null);
    const [open, setOpen] = useState<Set<number>>(() => new Set());

    const load = useCallback(async () => {
        setLoading(true); setErr(null);
        try {
            const { data } = await api.get(`/payroll/employees/${eid}/breakdown?year=${year}`);
            setMonths(data.months || []);
            // Auto-open the current month if present.
            const cur = new Date().getMonth() + 1;
            if ((data.months || []).some((m: Month) => m.month === cur)) setOpen(new Set([cur]));
        } catch (e: any) { setErr(formatApiError(e)); }
        finally { setLoading(false); }
    }, [eid, year]);
    useEffect(() => { load(); }, [load]);

    const toggle = (m: number) => setOpen(prev => {
        const n = new Set(prev);
        if (n.has(m)) n.delete(m); else n.add(m);
        return n;
    });

    return (
        <div className="space-y-6" data-testid="payroll-employee-page">
            <Link to="/hr/payroll" className="inline-flex items-center gap-1 text-sm" style={{ color: "var(--text-muted)" }} data-testid="pr-back">
                <ArrowLeft size={14} /> {t("common.back")}
            </Link>
            <div>
                <h1 className="text-3xl font-semibold" style={{ color: "var(--text)" }} data-testid="page-title">
                    {t("payroll.detail.title", { defaultValue: "Aylıq əməkhaqqı hesabatı" })}
                </h1>
                <p className="text-sm mt-1" style={{ color: "var(--text-muted)" }}>{year}</p>
            </div>
            {err && <div className="rounded-md p-3 text-sm" style={{ background: "var(--error-bg)", color: "var(--error-fg)" }}>{err}</div>}
            {loading ? <div className="p-8 flex justify-center"><Loader2 className="animate-spin" /></div> :
                months.length === 0 ? <div className="surface-card p-8 text-center text-sm" style={{ color: "var(--text-muted)" }}>{t("common.empty")}</div> : (
                    <div className="space-y-2" data-testid="payroll-months">
                        {months.map((m) => {
                            const isOpen = open.has(m.month);
                            return (
                                <div key={m.month} className="surface-card overflow-hidden" data-testid={`pr-month-${m.month}`}>
                                    <button type="button" onClick={() => toggle(m.month)}
                                        className="w-full flex items-center justify-between px-4 py-3 text-left"
                                        data-testid={`pr-month-toggle-${m.month}`}>
                                        <div className="flex items-center gap-2">
                                            {isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                                            <span className="text-sm font-medium" style={{ color: "var(--text)" }}>
                                                {t(`month.${m.month}` as any, { defaultValue: String(m.month) })} {year}
                                            </span>
                                            <span className="text-xs" style={{ color: "var(--text-muted)" }}>{m.sessions_count} {t("payroll.summary.col.sessions", { defaultValue: "sessiya" })}</span>
                                        </div>
                                        <div className="flex items-center gap-4 text-xs tabular-nums" style={{ color: "var(--text-muted)" }}>
                                            <span>{t("payroll.summary.col.accrued", { defaultValue: "Hesablanan" })}: <span style={{ color: "var(--text)" }}>{m.salary_masked ? "—" : fmtMoney(m.accrued_minor || 0, "AZN")}</span></span>
                                            <span>{t("payroll.summary.col.paid", { defaultValue: "Ödənilmiş" })}: <span style={{ color: "var(--text)" }}>{m.salary_masked ? "—" : fmtMoney(m.paid_minor || 0, "AZN")}</span></span>
                                            <span>{t("payroll.summary.col.remaining", { defaultValue: "Qalıq" })}: <span style={{ color: (m.remaining_minor || 0) > 0 ? "var(--warning-fg)" : "var(--text)" }}>{m.salary_masked ? "—" : fmtMoney(m.remaining_minor || 0, "AZN")}</span></span>
                                        </div>
                                    </button>
                                    {isOpen && (
                                        <table className="w-full text-sm border-t" style={{ borderColor: "var(--border)" }} data-testid={`pr-sessions-${m.month}`}>
                                            <thead>
                                                <tr className="text-left" style={{ background: "var(--input)", color: "var(--text-muted)" }}>
                                                    <th className="px-4 py-2">{t("session.date")}</th>
                                                    <th className="px-4 py-2 text-right">{t("payroll.detail.present", { defaultValue: "İştirak" })}</th>
                                                    <th className="px-4 py-2 text-right">{t("payroll.detail.per_student", { defaultValue: "Dərs qiyməti/şagird" })}</th>
                                                    <th className="px-4 py-2 text-right">%</th>
                                                    <th className="px-4 py-2 text-right">{t("payroll.detail.gross", { defaultValue: "Cəm" })}</th>
                                                    <th className="px-4 py-2 text-right">{t("payroll.detail.teacher_amount", { defaultValue: "Müəllim" })}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {m.sessions.map((s) => (
                                                    <tr key={s.accrual_id} className="border-t" style={{ borderColor: "var(--border)" }} data-testid={`pr-session-${s.accrual_id}`}>
                                                        <td className="px-4 py-3 text-xs" style={{ color: "var(--text-muted)" }}>{s.session_date}</td>
                                                        <td className="px-4 py-3 text-right tabular-nums">{s.present_count}</td>
                                                        <td className="px-4 py-3 text-right tabular-nums">{fmtMoney(s.per_student_lesson_price_minor, s.currency)}</td>
                                                        <td className="px-4 py-3 text-right tabular-nums">{s.teacher_share_percent != null ? `${s.teacher_share_percent}%` : "—"}</td>
                                                        <td className="px-4 py-3 text-right tabular-nums">{s.gross_amount_minor != null ? fmtMoney(s.gross_amount_minor, s.currency) : "—"}</td>
                                                        <td className="px-4 py-3 text-right tabular-nums" style={{ color: "var(--text)" }}>{s.amount_minor != null ? fmtMoney(s.amount_minor, s.currency) : "—"}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )
            }
        </div>
    );
}
